import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { businessInfo } from '../data/mock';
import useReveal from '../hooks/useReveal';

const care = [
  {
    title: 'Concierge Consultations',
    description:
      'Every journey opens with a private, education-first strategy session—never a script.',
  },
  {
    title: 'Customized Protocols',
    description:
      'Treatments curated around your body, goals, and lifestyle. No templates. Ever.',
  },
  {
    title: 'Private Boutique Setting',
    description:
      'Discreet, marble-warm surroundings designed to feel calm, intentional, and elevated.',
  },
  {
    title: 'Advanced Non-Invasive Technology',
    description:
      'The most refined modalities in aesthetic and wellness—no surgery, no downtime.',
  },
  {
    title: 'Transparent Guidance',
    description:
      'Honest recommendations, clear expectations, and ongoing wellness education.',
  },
  {
    title: 'Whole-Body Wellness',
    description:
      'Body contouring, lymphatic therapies, recovery rituals, and medical weight support—integrated.',
  },
];

const Pillar = ({ item, idx }) => {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} bg-carbon p-8 md:p-10 group hover:bg-onyx transition-colors duration-500`}
      style={{ '--reveal-delay': `${(idx % 3) * 90}ms` }}
      data-testid={`care-pillar-${idx}`}
    >
      <p className="font-serif brand-italic chrome-rose text-base mb-4">
        {String(idx + 1).padStart(2, '0')}
      </p>
      <h3 className="font-serif text-ivory text-xl md:text-2xl mb-4 leading-snug">
        {item.title}
      </h3>
      <p className="text-silverx text-sm md:text-base leading-relaxed">
        {item.description}
      </p>
    </div>
  );
};

const WhyChooseUs = () => {
  const headerReveal = useReveal();
  const ctaReveal = useReveal();

  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-28 md:py-36 bg-carbon overflow-hidden grain"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div
          ref={headerReveal.ref}
          className={`reveal ${headerReveal.visible ? 'is-visible' : ''} grid lg:grid-cols-12 gap-12 lg:gap-20 items-start mb-16`}
        >
          <div className="lg:col-span-5">
            <p className="eyebrow mb-6">
              <span className="inline-block w-8 h-px bg-silverx align-middle mr-3" />
              Our Standard
            </p>
            <h2 className="font-serif text-ivory text-4xl sm:text-5xl lg:text-[56px] leading-[1.06] text-balance">
              An Elevated
              <br />
              Standard of <span className="brand-italic chrome-rose">Care</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-8">
            <p className="text-platinum text-base md:text-lg leading-relaxed max-w-xl">
              We believe transformation should be strategic, elevated, and never
              one-size-fits-all. Every detail at Snatched Beauties Privé is designed to
              support your confidence, your wellness, and your results.
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-pewter/50 border border-pewter/50">
          {care.map((item, idx) => (
            <Pillar key={item.title} item={item} idx={idx} />
          ))}
        </div>

        {/* CTA */}
        <div
          ref={ctaReveal.ref}
          className={`reveal ${ctaReveal.visible ? 'is-visible' : ''} mt-20 flex flex-col md:flex-row items-start md:items-end md:justify-between gap-6`}
        >
          <div>
            <p className="eyebrow mb-4">Begin</p>
            <h3 className="font-serif text-ivory text-3xl md:text-[40px] leading-tight max-w-2xl text-balance">
              Your goals deserve more than
              <br />
              <span className="brand-italic chrome-rose">generic treatments.</span>
            </h3>
          </div>
          <a
            href={businessInfo.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="why-book-btn"
            className="btn-pill group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
          >
            Book Consultation
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
